'use client';

import React, { useState } from 'react';
import { useParticipants, useLocalParticipant, useRoomContext } from '@livekit/components-react';
import { X, Users, Mic, MicOff, Video, VideoOff, UserPlus, Search, Copy, Mail } from 'lucide-react';
import { CrownIcon } from '@/components/ui/crown-icon';
import { HostControls } from './HostControls';
import { useHostControl } from './HostControlContext';
import { resolveRoleFromMetadata } from './participantRoles';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import toast from 'react-hot-toast';

interface ParticipantsListProps {
  isOpen: boolean;
  onClose: () => void;
}

export function ParticipantsList({ isOpen, onClose }: ParticipantsListProps) {
  const participants = useParticipants();
  const localParticipant = useLocalParticipant();
  const room = useRoomContext();
  const { isHost, hasModeratorPermissions, isParticipantMuted, isParticipantVideoDisabled } =
    useHostControl();

  // Estados para busca e convite
  const [searchTerm, setSearchTerm] = useState('');
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteUrl, setInviteUrl] = useState('');
  const [copied, setCopied] = useState(false);
  const [activeMenuParticipant, setActiveMenuParticipant] = useState<string | null>(null);

  // Gerar URL de convite
  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      const origin = window.location.origin;
      const path = window.location.pathname;
      setInviteUrl(`${origin}${path}`);
    }
  }, []);

  // Função para copiar link
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      console.error(e);
    }
  };

  // Função para pedir ativação do som
  const handleRequestAudioActivation = async (
    participantName: string,
    participantIdentity: string,
  ) => {
    if (!room || !hasModeratorPermissions) return;

    try {
      // Encontrar o participante específico
      const targetParticipant = participants.find((p) => p.identity === participantIdentity);
      if (!targetParticipant) {
        toast.error('Participante não encontrado');
        return;
      }

      // Criar uma mensagem personalizada para o participante específico
      const message = {
        type: 'request-audio-activation',
        from: localParticipant.localParticipant?.identity || 'Anfitrião',
        to: participantIdentity,
        message: 'O anfitrião deseja que você ative o som',
        timestamp: Date.now(),
        targetParticipant: participantIdentity,
      };

      // Enviar via data channel com identificação específica
      await room.localParticipant?.publishData(new TextEncoder().encode(JSON.stringify(message)), {
        reliable: true,
      });

      toast.success(`Solicitação enviada para ${participantName}`);
    } catch (error) {
      console.error('Erro ao enviar solicitação:', error);
      toast.error('Erro ao enviar solicitação');
    }
  };

  // Função para silenciar participante
  const handleMuteParticipant = async (participantName: string, participantIdentity: string) => {
    if (!room || !hasModeratorPermissions) return;

    try {
      // Encontrar o participante específico
      const targetParticipant = participants.find((p) => p.identity === participantIdentity);
      if (!targetParticipant) {
        toast.error('Participante não encontrado');
        return;
      }

      // Criar uma mensagem de silenciamento
      const message = {
        type: 'mute-participant',
        from: localParticipant.localParticipant?.identity || 'Anfitrião',
        to: participantIdentity,
        message: `Você foi silenciado por ${localParticipant.localParticipant?.name || 'Anfitrião'}`,
        timestamp: Date.now(),
        targetParticipant: participantIdentity,
      };

      // Enviar via data channel
      await room.localParticipant?.publishData(new TextEncoder().encode(JSON.stringify(message)), {
        reliable: true,
      });

      // Silenciar o participante usando a API do LiveKit
      // Para participantes remotos, usamos o room para silenciar
      if (room && targetParticipant.identity !== localParticipant.localParticipant?.identity) {
        // Para participantes remotos, o silenciamento é feito via data channel
        // O participante remoto receberá a mensagem e se silenciará
        console.log(`Silenciando participante remoto: ${targetParticipant.identity}`);
      }

      toast.success(`${participantName} foi silenciado`);
    } catch (error) {
      console.error('Erro ao silenciar participante:', error);
      toast.error('Erro ao silenciar participante');
    }
  };

  // Função para desativar vídeo do participante
  const handleDisableVideo = async (participantName: string, participantIdentity: string) => {
    if (!room || !hasModeratorPermissions) return;

    try {
      // Encontrar o participante específico
      const targetParticipant = participants.find((p) => p.identity === participantIdentity);
      if (!targetParticipant) {
        toast.error('Participante não encontrado');
        return;
      }

      // Criar uma mensagem de desativação de vídeo
      const message = {
        type: 'disable-video',
        from: localParticipant.localParticipant?.identity || 'Anfitrião',
        to: participantIdentity,
        message: `Sua câmera foi desativada por ${localParticipant.localParticipant?.name || 'Anfitrião'}`,
        timestamp: Date.now(),
        targetParticipant: participantIdentity,
      };

      // Enviar via data channel
      await room.localParticipant?.publishData(new TextEncoder().encode(JSON.stringify(message)), {
        reliable: true,
      });

      // Para participantes remotos, o desligamento de vídeo é feito via data channel
      if (room && targetParticipant.identity !== localParticipant.localParticipant?.identity) {
        console.log(`Desativando vídeo do participante remoto: ${targetParticipant.identity}`);
      }

      toast.success(`Vídeo de ${participantName} foi desativado`);
    } catch (error) {
      console.error('Erro ao desativar vídeo:', error);
      toast.error('Erro ao desativar vídeo');
    }
  };

  // Função para silenciar todos os participantes
  const handleMuteAll = async () => {
    if (!room || !hasModeratorPermissions) return;

    try {
      // Filtrar apenas participantes remotos (não incluir o anfitrião)
      const remoteParticipants = participants.filter(
        (p) => p.identity !== localParticipant.localParticipant?.identity,
      );

      if (remoteParticipants.length === 0) {
        toast('Não há outros participantes para silenciar', { icon: 'ℹ️' });
        return;
      }

      // Enviar mensagem de silenciamento para todos os participantes remotos
      const muteAllMessage = {
        type: 'mute-all',
        from: localParticipant.localParticipant?.identity || 'Anfitrião',
        message: `Todos foram silenciados por ${localParticipant.localParticipant?.name || 'Anfitrião'}`,
        timestamp: Date.now(),
        targetParticipants: remoteParticipants.map((p) => p.identity),
      };

      // Enviar via data channel para todos
      await room.localParticipant?.publishData(
        new TextEncoder().encode(JSON.stringify(muteAllMessage)),
        { reliable: true },
      );

      toast.success(`${remoteParticipants.length} participantes foram silenciados`);
    } catch (error) {
      console.error('Erro ao silenciar todos:', error);
      toast.error('Erro ao silenciar todos os participantes');
    }
  };

  // Force the participants panel width
  React.useEffect(() => {
    if (!isOpen) return;

    const participantsPanel = document.querySelector(
      '.lk-participants-panel',
    ) as HTMLElement | null;
    if (participantsPanel) {
      participantsPanel.style.width = '320px';
      participantsPanel.style.minWidth = '320px';
      participantsPanel.style.maxWidth = '320px';
    }
  }, [isOpen]);

  // Filtrar participantes baseado na busca
  const filteredParticipants = participants.filter((participant) => {
    const displayName = participant.name || participant.identity;
    return displayName.toLowerCase().includes(searchTerm.toLowerCase());
  });

  if (!isOpen) return null;

  const headerParticipants = document.querySelector(
    '.lk-participants-header',
  ) as HTMLElement | null;

  if (headerParticipants) {
    headerParticipants.style.display = 'flex';
    headerParticipants.style.alignItems = 'center';
    headerParticipants.style.justifyContent = 'space-between';
    headerParticipants.style.padding = '16px';
  }

  return (
    <div className="lk-participants-panel flex flex-col h-full">
      {/* Header */}
      <div className="lk-participants-header">
        <div className="flex items-center gap-2">
          <Users className="w-5 h-5" />
          <h2 className="text-lg font-semibold text-white">
            Participantes ({filteredParticipants.length})
          </h2>
        </div>
        <button
          onClick={onClose}
          className="p-1 hover:bg-neutral-700 rounded-full transition-colors"
        >
          <X className="w-5 h-5 text-gray-400" />
        </button>
      </div>

      {/* Controles de busca e convite */}
      <div className="p-4 border-b border-neutral-700">
        {/* Botão Convidar */}
        <div className="mb-3">
          <Button
            variant="outline"
            size="default"
            onClick={() => setInviteOpen(true)}
            className="w-full h-12 bg-blue-600 border-blue-600 text-white hover:bg-blue-700 hover:border-blue-700 font-bold !font-bold"
            style={{ fontWeight: 'bold' }}
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Convidar alguém
          </Button>
        </div>

        {/* Campo de busca */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            type="text"
            placeholder="Buscar participantes..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 bg-neutral-800 border-neutral-600 text-white placeholder-gray-400 focus:border-blue-500"
          />
        </div>
      </div>

      {/* Participants List - Scrollable area */}
      <div className="lk-participants-messages flex-1 overflow-y-auto">
        <div className="space-y-4 p-4">
          {filteredParticipants.map((participant) => {
            const isLocal = participant === localParticipant.localParticipant;
            const displayName = participant.name || participant.identity;

            // Check if participant has audio/video enabled
            const hasAudio =
              participant.isMicrophoneEnabled && !isParticipantMuted(participant.identity);
            const hasVideo =
              participant.isCameraEnabled && !isParticipantVideoDisabled(participant.identity);
            const participantRole = resolveRoleFromMetadata(participant.metadata);
            const isParticipantHost = participantRole === 'host';
            const isParticipantCoHost = participantRole === 'co-host';

            const onRequestAudio = () =>
              handleRequestAudioActivation(displayName, participant.identity);
            const onMute = () => handleMuteParticipant(displayName, participant.identity);
            const onDisableVideo = () => handleDisableVideo(displayName, participant.identity);

            const hostControlsProps = {
              participant,
              isLocal,
              displayName,
              hasAudio,
              hasVideo,
              onRequestAudio,
              onMute,
              onDisableVideo,
              onMenuOpenChange: (open: boolean) => {
                setActiveMenuParticipant((prev) => {
                  if (open) {
                    return participant.identity;
                  }
                  return prev === participant.identity ? null : prev;
                });
              },
            };
            const isMenuOpen = activeMenuParticipant === participant.identity;

            return (
              <div
                key={participant.identity}
                className="lk-participant-entry hover:bg-neutral-800 transition-colors duration-200 cursor-pointer rounded-lg p-3 -m-3 group relative"
              >
                <div className="flex items-center gap-4">
                  {/* Avatar */}
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center ${(() => {
                      const colors = [
                        'bg-blue-600',
                        'bg-green-600',
                        'bg-purple-600',
                        'bg-pink-600',
                        'bg-indigo-600',
                        'bg-red-600',
                        'bg-yellow-600',
                        'bg-teal-600',
                        'bg-orange-600',
                        'bg-cyan-600',
                      ];
                      // Use o hash do nome para gerar uma cor consistente
                      let hash = 0;
                      for (let i = 0; i < displayName.length; i++) {
                        hash = displayName.charCodeAt(i) + ((hash << 5) - hash);
                      }
                      return colors[Math.abs(hash) % colors.length];
                    })()}`}
                  >
                    <span className="text-white font-medium text-sm">
                      {displayName.charAt(0).toUpperCase()}
                    </span>
                  </div>

                  {/* Name and status */}
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-white font-medium">{displayName}</span>
                      {isLocal && (
                        <span className="text-xs bg-blue-600 text-white px-2 py-1 rounded-full">
                          Você
                        </span>
                      )}
                    </div>
                    {/* Participant Role */}
                    <div className="text-sm text-gray-400 flex items-center gap-1">
                      {isParticipantHost && (
                        <div className="flex items-center gap-1">
                          <CrownIcon className="text-yellow-500" size={12} />
                          <span className="text-yellow-500 font-medium">Anfitrião</span>
                        </div>
                      )}
                      {isParticipantCoHost && (
                        <div className="flex items-center gap-1">
                          <CrownIcon className="text-blue-400" size={12} />
                          <span className="text-blue-400 font-medium">Co-Anfitrião</span>
                        </div>
                      )}
                      {!isParticipantHost && !isParticipantCoHost && 'Convidado'}
                    </div>
                  </div>

                  {/* Audio/Video Status or Host Controls - Hidden on hover */}
                  <div className="flex items-center gap-3 group-hover:opacity-0 transition-opacity duration-200">
                    <div
                      className={`p-1.5 rounded-full ${hasAudio ? 'bg-green-600' : 'bg-red-600'}`}
                    >
                      {hasAudio ? (
                        <Mic className="w-3 h-3 text-white" />
                      ) : (
                        <MicOff className="w-3 h-3 text-white" />
                      )}
                    </div>
                    <div
                      className={`p-1.5 rounded-full ${hasVideo ? 'bg-green-600' : 'bg-red-600'}`}
                    >
                      {hasVideo ? (
                        <Video className="w-3 h-3 text-white" />
                      ) : (
                        <VideoOff className="w-3 h-3 text-white" />
                      )}
                    </div>
                  </div>

                  {/* Botão "Pedir para ativar som" - Aparece apenas para o anfitrião quando convidados têm áudio e vídeo desabilitados */}
                  {!isLocal && !hasAudio && !hasVideo && hasModeratorPermissions && (
                    <div
                      className={cn(
                        'absolute inset-0 flex items-center justify-center transition-opacity duration-200 bg-black/30 rounded-lg',
                        isMenuOpen ? 'opacity-100' : 'opacity-0 group-hover:opacity-100',
                      )}
                    >
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="bg-blue-500 border-blue-500 text-white hover:bg-blue-600 hover:border-blue-600 text-xs px-3 py-1 h-7 shadow-xl font-bold !font-bold"
                          style={{ fontWeight: 'bold' }}
                          onClick={(e) => {
                            e.stopPropagation();
                            onRequestAudio();
                          }}
                        >
                          Pedir para ativar som
                        </Button>
                        <HostControls
                          {...hostControlsProps}
                          variant="dropdown-only"
                          dropdownTriggerClassName="h-7 px-3 text-xs font-bold !font-bold bg-blue-500 border border-blue-500 text-white hover:bg-blue-600 hover:border-blue-600 shadow-xl"
                        />
                      </div>
                    </div>
                  )}

                  {/* Botão "Silenciar" - Aparece apenas para o anfitrião quando convidados têm som ativo */}
                  {!isLocal && hasAudio && hasModeratorPermissions && (
                    <div
                      className={cn(
                        'absolute inset-0 flex items-center justify-center transition-opacity duration-200 bg-black/30 rounded-lg',
                        isMenuOpen ? 'opacity-100' : 'opacity-0 group-hover:opacity-100',
                      )}
                    >
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="bg-red-500 border-red-500 text-white hover:bg-red-600 hover:border-red-600 text-xs px-3 py-1 h-7 shadow-xl font-bold !font-bold"
                          style={{ fontWeight: 'bold' }}
                          onClick={(e) => {
                            e.stopPropagation();
                            onMute();
                          }}
                        >
                          Silenciar
                        </Button>
                        <HostControls
                          {...hostControlsProps}
                          variant="dropdown-only"
                          dropdownTriggerClassName="h-7 px-3 text-xs font-bold !font-bold bg-red-500 border border-red-500 text-white hover:bg-red-600 hover:border-red-600 shadow-xl"
                        />
                      </div>
                    </div>
                  )}

                  {/* Botão "Parar vídeo" - Aparece apenas para o anfitrião quando convidados têm vídeo ativo */}
                  {!isLocal && hasVideo && hasModeratorPermissions && (
                    <div
                      className={cn(
                        'absolute inset-0 flex items-center justify-center transition-opacity duration-200 bg-black/30 rounded-lg',
                        isMenuOpen ? 'opacity-100' : 'opacity-0 group-hover:opacity-100',
                      )}
                    >
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="bg-orange-500 border-orange-500 text-white hover:bg-orange-600 hover:border-orange-600 text-xs px-3 py-1 h-7 shadow-xl font-bold !font-bold"
                          style={{ fontWeight: 'bold' }}
                          onClick={(e) => {
                            e.stopPropagation();
                            onDisableVideo();
                          }}
                        >
                          Parar vídeo
                        </Button>
                        <HostControls
                          {...hostControlsProps}
                          variant="dropdown-only"
                          dropdownTriggerClassName="h-7 px-3 text-xs font-bold !font-bold bg-orange-500 border border-orange-500 text-white hover:bg-orange-600 hover:border-orange-600 shadow-xl"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {filteredParticipants.length === 0 && (
            <div className="text-center py-8">
              <Users className="w-12 h-12 text-gray-500 mx-auto mb-3" />
              <p className="text-gray-400">
                {searchTerm ? 'Nenhum participante encontrado' : 'Nenhum participante na reunião'}
              </p>
              {searchTerm && (
                <p className="text-sm text-gray-500 mt-2">Tente ajustar os termos de busca</p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Rodapé fixo com botão Silenciar todos */}
      {(isHost || hasModeratorPermissions) && (
        <div className="border-t border-neutral-700 p-4 bg-neutral-900 flex-shrink-0">
          <Button
            variant="outline"
            size="default"
            onClick={handleMuteAll}
            className="w-full h-10 bg-red-600 border-red-600 text-white hover:bg-red-700 hover:border-red-700 font-bold !font-bold"
            style={{ fontWeight: 'bold' }}
          >
            <MicOff className="w-4 h-4 mr-2" />
            Silenciar todos
          </Button>
        </div>
      )}

      {/* Modal de Convite */}
      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent className="max-w-md bg-neutral-800 border-neutral-700 text-white">
          <DialogHeader>
            <DialogTitle className="text-white">Convide mais pessoas</DialogTitle>
            <DialogDescription className="text-neutral-300">
              Compartilhando o link da reunião
            </DialogDescription>
          </DialogHeader>

          <div className="flex items-center gap-2">
            <Input
              readOnly
              value={inviteUrl}
              className="flex-1 bg-neutral-700 border-neutral-600 text-white"
            />
            <Button
              onClick={handleCopyLink}
              variant="secondary"
              className="shrink-0 bg-blue-500 hover:bg-blue-600"
            >
              <Copy className="w-4 h-4 mr-1" />
              {copied ? 'Copiado' : 'Copiar'}
            </Button>
          </div>

          <div className="mt-4">
            <p className="text-sm text-neutral-300 mb-3">Compartilhar convite da reunião</p>
            <div className="flex items-center gap-3">
              <a
                href={`mailto:?subject=${encodeURIComponent('Convite para reunião')}&body=${encodeURIComponent('Entre na reunião: ' + inviteUrl)}`}
                target="_blank"
                rel="noreferrer"
              >
                <Button
                  variant="outline"
                  size="icon"
                  title="E-mail padrão"
                  className="border-neutral-600 text-white hover:bg-neutral-700"
                >
                  <Mail className="w-4 h-4" />
                </Button>
              </a>
              <a
                href={`https://mail.google.com/mail/?view=cm&fs=1&su=${encodeURIComponent('Convite para reunião')}&body=${encodeURIComponent('Entre na reunião: ' + inviteUrl)}`}
                target="_blank"
                rel="noreferrer"
              >
                <Button
                  variant="outline"
                  size="icon"
                  title="Gmail"
                  className="border-neutral-600 text-white hover:bg-neutral-700"
                >
                  G
                </Button>
              </a>
              <a
                href={`https://outlook.live.com/mail/deeplink/compose?subject=${encodeURIComponent('Convite para reunião')}&body=${encodeURIComponent('Entre na reunião: ' + inviteUrl)}`}
                target="_blank"
                rel="noreferrer"
              >
                <Button
                  variant="outline"
                  size="icon"
                  title="Outlook"
                  className="border-neutral-600 text-white hover:bg-neutral-700"
                >
                  O
                </Button>
              </a>
              <a
                href={`https://compose.mail.yahoo.com/?subject=${encodeURIComponent('Convite para reunião')}&body=${encodeURIComponent('Entre na reunião: ' + inviteUrl)}`}
                target="_blank"
                rel="noreferrer"
              >
                <Button
                  variant="outline"
                  size="icon"
                  title="Yahoo Mail"
                  className="border-neutral-600 text-white hover:bg-neutral-700"
                >
                  Y
                </Button>
              </a>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
